// Hyperliquid WebSocket API Service

// WebSocket数据类型定义
export interface WsLevel {
  px: string; // price
  sz: string; // size
  n: number; // number of orders
}

export interface WsBook {
  coin: string;
  levels: [Array<WsLevel>, Array<WsLevel>]; // [bids, asks]
  time: number;
}

export interface WsTrade {
  coin: string;
  side: string;
  px: string;
  sz: string;
  hash: string;
  time: number;
  tid: number;
  users: [string, string]; // [buyer, seller]
}

export interface AllMids {
  mids: Record<string, string>;
}
export interface WsCandle {
  t: number; // open millis
  T: number; // close millis
  s: string; // coin
  i: string; // interval
  o: number; // open price
  c: number; // close price
  h: number; // high price
  l: number; // low price
  v: number; // volume (base unit)
  n: number; // number of trades
}

export interface WsBbo {
  coin: string;
  time: number;
  bbo: [WsLevel | null, WsLevel | null];
}

// ActiveAssetData types for user's leverage and trading data per coin
export interface ActiveAssetData {
  user: string;
  coin: string;
  leverage: {
    type: 'cross' | 'isolated';
    value: number;
  };
  maxTradeSzs: [string, string]; // [long, short]
  availableToTrade: [string, string]; // [long, short]
}

// 订阅消息类型
export interface SubscriptionMessage {
  method: 'subscribe' | 'unsubscribe';
  subscription: {
    type: string;
    coin?: string;
    user?: string;
    interval?: string;
    dex?: string;
  };
}

// WebSocket响应消息类型
export interface WebSocketMessage {
  channel: string;
  data: any;
  isSnapshot?: boolean;
}

// WebSocket连接管理类
export class HyperliquidWebSocket {
  private ws: WebSocket | null = null;
  private isConnected = false;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private subscriptions = new Set<string>();
  private messageHandlers = new Map<string, Set<(data: any) => void>>();

  // Hyperliquid WebSocket URL
  private readonly WS_URL = 'wss://api.hyperliquid.xyz/ws';

  constructor() {
    this.connect();
  }

  private connect() {
    try {
      this.ws = new WebSocket(this.WS_URL);

      this.ws.onopen = () => {
        console.log('WebSocket connected to Hyperliquid');
        this.isConnected = true;
        this.reconnectAttempts = 0;

        // 重新订阅之前的频道
        this.resubscribeAll();
      };

      this.ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          // console.log('HyperliquidWebSocket - Raw message received:', message);

          // Hyperliquid WebSocket messages have a different format than our interface
          // They typically have 'channel' as the subscription type and 'data' as the actual data
          this.handleMessage(message);
        } catch (error) {
          console.error(
            'Failed to parse WebSocket message:',
            error,
            'Raw data:',
            event.data,
          );
        }
      };

      this.ws.onclose = () => {
        console.log('WebSocket disconnected');
        this.isConnected = false;
        this.attemptReconnect();
      };

      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
      this.attemptReconnect();
    }
  }

  private attemptReconnect() {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      console.log(
        `Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`,
      );

      setTimeout(() => {
        this.connect();
      }, this.reconnectDelay * this.reconnectAttempts);
    } else {
      console.error('Max reconnection attempts reached');
    }
  }

  private resubscribeAll() {
    // 重新订阅所有之前的订阅
    console.log('Resubscribing to', this.subscriptions.size, 'subscriptions');
    this.subscriptions.forEach((subscription) => {
      try {
        const subscriptionObj = JSON.parse(subscription);
        this.sendSubscription(subscriptionObj);
      } catch (error) {
        console.error('Failed to resubscribe:', error);
      }
    });
  }

  private handleMessage(message: WebSocketMessage) {
    const { channel, data } = message;

    // Add debugging for activeAssetData messages
    if (channel === 'activeAssetData') {
      // console.log('HyperliquidWebSocket - received activeAssetData message:', message);
    }

    // 获取该频道的所有处理器
    const handlers = this.messageHandlers.get(channel);
    if (handlers) {
      handlers.forEach((handler) => handler(data));
    } else {
      // Log when no handlers are found for debugging
      // console.log('HyperliquidWebSocket - no handlers found for channel:', channel);
    }
  }

  private sendSubscription(subscription: SubscriptionMessage['subscription']) {
    if (!this.ws) {
      console.warn('WebSocket instance not available');
      return;
    }

    if (!this.isConnected) {
      console.warn(
        'WebSocket not connected, queuing subscription:',
        subscription,
      );
      // 保存订阅以便连接时发送
      this.subscriptions.add(JSON.stringify(subscription));
      return;
    }

    const message: SubscriptionMessage = {
      method: 'subscribe',
      subscription,
    };

    console.log('Sending subscription:', message);
    this.ws.send(JSON.stringify(message));

    // 保存订阅以便重连时重新订阅
    this.subscriptions.add(JSON.stringify(subscription));
  }

  private sendUnsubscription(
    subscription: SubscriptionMessage['subscription'],
  ) {
    if (!this.isConnected || !this.ws) {
      console.warn('WebSocket not connected, cannot send unsubscription');
      return;
    }

    const message: SubscriptionMessage = {
      method: 'unsubscribe',
      subscription,
    };

    this.ws.send(JSON.stringify(message));

    // 从订阅列表中移除
    this.subscriptions.delete(JSON.stringify(subscription));
  }

  // 订阅L2 OrderBook数据
  subscribeToOrderBook(coin: string, callback: (data: WsBook) => void) {
    const subscription = {
      type: 'l2Book',
      coin,
    };

    // 添加消息处理器
    const channel = 'l2Book';
    if (!this.messageHandlers.has(channel)) {
      this.messageHandlers.set(channel, new Set());
    }
    this.messageHandlers.get(channel)!.add(callback);

    // 发送订阅
    this.sendSubscription(subscription);

    // 返回取消订阅的函数
    return () => {
      this.messageHandlers.get(channel)?.delete(callback);
      if (this.messageHandlers.get(channel)?.size === 0) {
        this.sendUnsubscription(subscription);
      }
    };
  }

  // 订阅交易数据
  subscribeToTrades(coin: string, callback: (data: WsTrade[]) => void) {
    const subscription = {
      type: 'trades',
      coin,
    };

    const channel = 'trades';
    if (!this.messageHandlers.has(channel)) {
      this.messageHandlers.set(channel, new Set());
    }
    this.messageHandlers.get(channel)!.add(callback);

    this.sendSubscription(subscription);

    return () => {
      this.messageHandlers.get(channel)?.delete(callback);
      if (this.messageHandlers.get(channel)?.size === 0) {
        this.sendUnsubscription(subscription);
      }
    };
  }
  subscribeToCandle(
    coin: string,
    interval: string,
    callback: (data: WsCandle) => void,
  ) {
    const subscription = {
      type: 'candle',
      coin,
      interval,
    };

    // 添加消息处理器
    const channel = 'candle';
    if (!this.messageHandlers.has(channel)) {
      this.messageHandlers.set(channel, new Set());
    }
    this.messageHandlers.get(channel)!.add(callback);

    // 发送订阅
    this.sendSubscription(subscription);

    // 返回取消订阅的函数
    return () => {
      this.messageHandlers.get(channel)?.delete(callback);
      if (this.messageHandlers.get(channel)?.size === 0) {
        this.sendUnsubscription(subscription);
      }
    };
  }
  // 订阅所有中间价格
  subscribeToAllMids(callback: (data: AllMids) => void, dex?: string) {
    const subscription = {
      type: 'allMids',
      ...(dex && { dex }),
    };

    const channel = 'allMids';
    if (!this.messageHandlers.has(channel)) {
      this.messageHandlers.set(channel, new Set());
    }
    this.messageHandlers.get(channel)!.add(callback);

    this.sendSubscription(subscription);

    return () => {
      this.messageHandlers.get(channel)?.delete(callback);
      if (this.messageHandlers.get(channel)?.size === 0) {
        this.sendUnsubscription(subscription);
      }
    };
  }

  // 订阅BBO (Best Bid Offer)
  subscribeToBbo(coin: string, callback: (data: WsBbo) => void) {
    const subscription = {
      type: 'bbo',
      coin,
    };

    const channel = 'bbo';
    if (!this.messageHandlers.has(channel)) {
      this.messageHandlers.set(channel, new Set());
    }
    this.messageHandlers.get(channel)!.add(callback);

    this.sendSubscription(subscription);

    return () => {
      this.messageHandlers.get(channel)?.delete(callback);
      if (this.messageHandlers.get(channel)?.size === 0) {
        this.sendUnsubscription(subscription);
      }
    };
  }

  // 订阅ActiveAssetData (用户特定币种的杠杆和交易数据)
  subscribeToActiveAssetData(
    user: string,
    coin: string,
    callback: (data: ActiveAssetData) => void,
  ) {
    console.log(
      'HyperliquidWebSocket - subscribing to activeAssetData for user:',
      user,
      'coin:',
      coin,
    );

    const subscription = {
      type: 'activeAssetData',
      user,
      coin,
    };

    const channel = 'activeAssetData';
    if (!this.messageHandlers.has(channel)) {
      this.messageHandlers.set(channel, new Set());
    }
    this.messageHandlers.get(channel)!.add(callback);

    console.log(
      'HyperliquidWebSocket - sending activeAssetData subscription:',
      subscription,
    );
    this.sendSubscription(subscription);

    return () => {
      console.log(
        'HyperliquidWebSocket - unsubscribing from activeAssetData for user:',
        user,
        'coin:',
        coin,
      );
      this.messageHandlers.get(channel)?.delete(callback);
      if (this.messageHandlers.get(channel)?.size === 0) {
        this.sendUnsubscription(subscription);
      }
    };
  }

  // 断开连接
  disconnect() {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
      this.isConnected = false;
      this.subscriptions.clear();
      this.messageHandlers.clear();
    }
  }

  // 获取连接状态
  getConnectionStatus() {
    return this.isConnected;
  }
}

// 全局WebSocket实例
let globalWsInstance: HyperliquidWebSocket | null = null;

export function getWebSocketInstance(): HyperliquidWebSocket {
  if (typeof window === 'undefined') {
    throw new Error('WebSocket can only be used in browser environment');
  }

  if (!globalWsInstance) {
    console.log('Creating new WebSocket instance');
    globalWsInstance = new HyperliquidWebSocket();
  }
  return globalWsInstance;
}

// 清理WebSocket连接（用于应用关闭时）
export function cleanupWebSocket() {
  if (globalWsInstance) {
    globalWsInstance.disconnect();
    globalWsInstance = null;
  }
}
